import React, { useState, useEffect, useRef } from 'react';
import './TopBar.css';

const HamburgerIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="6" x2="21" y2="6"/>
    <line x1="3" y1="12" x2="21" y2="12"/>
    <line x1="3" y1="18" x2="21" y2="18"/>
  </svg>
);

const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/>
    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const BellIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
  </svg>
);

const HelpIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
    <circle cx="12" cy="17" r="0.5" fill="currentColor"/>
  </svg>
);

// Actividad/pulso (lucide) — panel de salud.
const SaludIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
  </svg>
);

// Sparkles (lucide) — Hunter. Solo móvil (el sidebar lo cubre en desktop).
const SparklesIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .962 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.962 0z"/>
    <path d="M20 3v4"/><path d="M22 5h-4"/><path d="M4 17v2"/><path d="M5 18H3"/>
  </svg>
);

function timeAgo(date) {
  const diff = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (diff < 60)    return 'ahora';
  if (diff < 3600)  return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  return `hace ${Math.floor(diff / 86400)} d`;
}

function fmtMoney(n) {
  return `$${parseFloat(n || 0).toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;
}

const ROLE_LABELS = { admin: 'Administrador', coordinador: 'Coordinador', vendedor: 'Vendedor' };

function TopBar({
  onOpenSearch, userName, userRole, onLogout, onMobileMenu,
  notifications = [], unreadCount = 0, onMarkAllRead, onNavigateOrders,
  onOpenAssistant, assistantName = 'Hunter',
  prefetchActive = false,
  mostrarSalud = false, saludCount = 0, onOpenSalud,
}) {
  const hunterName = (assistantName && assistantName.trim()) || 'Hunter';
  const [menuOpen, setMenuOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const wrapRef = useRef(null);
  const bellRef = useRef(null);

  const initials = userName ? userName.slice(0, 2).toUpperCase() : '??';

  // Click-outside for user dropdown
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  // Click-outside for bell panel
  useEffect(() => {
    if (!bellOpen) return;
    const handler = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) setBellOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [bellOpen]);

  const handleBellClick = () => {
    const opening = !bellOpen;
    setBellOpen(opening);
    if (opening && unreadCount > 0) onMarkAllRead?.();
  };

  return (
    <header className="topbar">
      {/* Hamburguesa — solo visible en móvil */}
      <button
        className="topbar-hamburger"
        onClick={onMobileMenu}
        aria-label="Abrir menú"
      >
        <HamburgerIcon />
      </button>

      {/* P1d (PUL-OCT): el título colapsado SE RETIRÓ — la barra lleva solo buscador e iconos;
          el título de cada página vive fijo debajo, en su PageHeader/hero (patrón Ventas). */}

      {/* Buscador global — trigger */}
      <button
        className="topbar-search-trigger"
        onClick={onOpenSearch}
        aria-label="Búsqueda global (Ctrl+K)"
      >
        <span className="topbar-search-trigger-icon"><SearchIcon /></span>
        <span className="topbar-search-trigger-text">Buscar en todo…</span>
        <kbd className="topbar-search-trigger-kbd">Ctrl+K</kbd>
      </button>

      {/* Acciones + usuario */}
      <div className="topbar-actions">
        {prefetchActive && <span className="topbar-prefetch-dot" title="Precargando datos…" />}

        {/* Hunter — entrada móvil (F4 §14). Solo <768px vía CSS: en desktop el
            disparador vive en el sidebar. Un tap, siempre visible, junto a las
            acciones globales — no en el tab-bar (Hunter es capacidad, no destino). */}
        {onOpenAssistant && (
          <button
            className="topbar-icon-btn topbar-hunter-btn"
            title={`Abrir ${hunterName}`}
            aria-label={`Abrir asistente ${hunterName}`}
            onClick={onOpenAssistant}
          >
            <SparklesIcon />
          </button>
        )}

        {/* Panel de salud (admin). El ícono está siempre para que el estado vacío
            «todo al día» sea alcanzable; el BADGE con el número aparece sólo si > 0. */}
        {mostrarSalud && (
          <button
            className="topbar-icon-btn topbar-salud-btn"
            title="Panel de salud"
            aria-label={saludCount > 0 ? `Panel de salud: ${saludCount} pendiente(s)` : 'Panel de salud: todo al día'}
            onClick={onOpenSalud}
          >
            <SaludIcon />
            {saludCount > 0 && (
              <span className="topbar-badge topbar-badge--count">{saludCount > 9 ? '9+' : saludCount}</span>
            )}
          </button>
        )}

        <button className="topbar-icon-btn topbar-help-btn" title="Ayuda" aria-label="Ayuda">
          <HelpIcon />
        </button>

        {/* Bell + notification panel */}
        <div className="topbar-bell-wrap" ref={bellRef}>
          <button
            className={`topbar-icon-btn${bellOpen ? ' topbar-icon-btn--active' : ''}`}
            title="Notificaciones"
            aria-label="Notificaciones"
            onClick={handleBellClick}
          >
            <BellIcon />
            {unreadCount > 0 && (
              <span className="topbar-badge topbar-badge--count">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
            {unreadCount === 0 && notifications.length > 0 && (
              <span className="topbar-badge" />
            )}
          </button>

          {bellOpen && (
            <div className="topbar-notif-panel" role="dialog" aria-label="Notificaciones">
              <div className="topbar-notif-header">
                <span className="topbar-notif-title">Notificaciones</span>
                {notifications.length > 0 && (
                  <button
                    className="topbar-notif-mark-read"
                    onClick={onMarkAllRead}
                  >
                    Marcar todo leído
                  </button>
                )}
              </div>

              {notifications.length === 0 ? (
                <div className="topbar-notif-empty">
                  <span className="topbar-notif-empty-icon">✓</span>
                  <span>Todo al día</span>
                  <span className="topbar-notif-empty-sub">No hay notificaciones nuevas.</span>
                </div>
              ) : (
                <div className="topbar-notif-list">
                  {notifications.map((n) => (
                    <div
                      key={n.id}
                      className={`topbar-notif-item${n.read ? '' : ' topbar-notif-item--unread'}`}
                    >
                      <div className="topbar-notif-row">
                        <span className="topbar-notif-num">#{n.orderNumber}</span>
                        <span className="topbar-notif-total">{fmtMoney(n.rawTotal)}</span>
                        <span className="topbar-notif-time">{timeAgo(n.time)}</span>
                      </div>
                      <div className="topbar-notif-meta">
                        {n.payment} · {n.origin}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <button
                className="topbar-notif-view-all"
                onClick={() => { onNavigateOrders?.(); setBellOpen(false); }}
              >
                Ver todos los pedidos →
              </button>
            </div>
          )}
        </div>

        <div className="topbar-divider" />

        <div className="topbar-user-wrap" ref={wrapRef}>
          <button
            className={`topbar-user${menuOpen ? ' topbar-user--open' : ''}`}
            onClick={() => setMenuOpen((v) => !v)}
            aria-haspopup="true"
            aria-expanded={menuOpen}
            title={userName}
          >
            <div className="topbar-avatar">{initials}</div>
            <span className="topbar-username">{userName}</span>
            <span className="topbar-chevron">▾</span>
          </button>

          {menuOpen && (
            <div className="topbar-dropdown" role="menu">
              <div className="topbar-dropdown-header">{userName}</div>
              {userRole && (
                <div className="topbar-dropdown-role">{ROLE_LABELS[userRole] || userRole}</div>
              )}
              <div className="topbar-dropdown-divider" />
              <button
                className="topbar-dropdown-item topbar-dropdown-logout"
                role="menuitem"
                onClick={() => { setMenuOpen(false); onLogout && onLogout(); }}
              >
                <span aria-hidden="true">↩</span> Cerrar sesión
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export default TopBar;
